import type { RealtimeChannel } from '@supabase/supabase-js';
import { getDbSupabase, isSupabaseConfigured } from './supabase';

const CHANNEL_NAME = 'acra-sync-live';

type AcraSyncListener = {
  onlyOnComplete: boolean;
  debounceMs: number;
  onChange: () => void;
  timer: number | null;
};

let channel: RealtimeChannel | null = null;
let listeners = new Map<number, AcraSyncListener>();
let nextListenerId = 1;

function notifyListeners(payload?: { new?: Record<string, unknown> }) {
  for (const listener of listeners.values()) {
    if (listener.onlyOnComplete) {
      const status = String(payload?.new?.status ?? '').trim().toLowerCase();
      if (status !== 'complete' && status !== 'failed') continue;
    }
    if (listener.timer != null) window.clearTimeout(listener.timer);
    listener.timer = window.setTimeout(() => {
      listener.onChange();
      listener.timer = null;
    }, listener.debounceMs);
  }
}

function ensureChannel() {
  if (channel || !isSupabaseConfigured) return;

  const db = getDbSupabase('crm');
  channel = db
    .channel(CHANNEL_NAME)
    .on(
      'postgres_changes',
      { event: '*', schema: 'crm', table: 'acra_entities_sync_state' },
      (payload) => notifyListeners(payload as { new?: Record<string, unknown> }),
    )
    .subscribe();
}

function teardownChannelIfIdle() {
  if (listeners.size > 0 || !channel) return;
  void getDbSupabase('crm').removeChannel(channel);
  channel = null;
}

export function subscribeAcraSyncRealtime(
  onChange: () => void,
  options: { onlyOnComplete?: boolean; debounceMs?: number } = {},
): () => void {
  const id = nextListenerId++;
  listeners.set(id, {
    onlyOnComplete: options.onlyOnComplete ?? false,
    debounceMs: options.debounceMs ?? 4000,
    onChange,
    timer: null,
  });
  ensureChannel();

  return () => {
    const listener = listeners.get(id);
    if (listener?.timer != null) window.clearTimeout(listener.timer);
    listeners.delete(id);
    teardownChannelIfIdle();
  };
}
